import React, { useState, useEffect, useRef, useContext } from "react";
import { useFormContext } from "../createworkflow/FormContext";
import MenuContext from "../createworkflow/MenuContext";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { fetchWorkflows, stage1fileupload } from "../../services/ApiWorkflow";
import { getCookie } from "../../services/DataRequestService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import AuthService from "../../services/AuthService";
import { getValue } from "../../Utils/helper";
import { stageLayoutStyles, stageFormStyles, StageNavButtons } from "./helper";
import UserService from "../../services/UserService";
import WorkflowTableModal from "./WorkflowTableModal";
import { useNavigate } from "react-router-dom";
import QCModal from "../QC/QCModal";
import Base from "../QC/Base";

const { REACT_APP_API_BASE_URL } = process.env;

export default function Stage1() {
  const refWorkflowName = useRef();
  const refLblerrWorkflowName = useRef();
  const { formData, updateFormData } = useFormContext();
  const { setActiveTab } = useContext(MenuContext);
  const queryClient = useQueryClient();

  const [existingWorkFlowData, setExistingWorkflowData] = useState(null);
  const [uploadOption, setUploadOption] = useState("use_existing");
  const [workFlowdata, setWorkFlowData] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate()


  const [openQC, setOpenQC] = useState(false);


  // inside Stage1 component
  useEffect(() => {
    // 1) load cached workflows immediately (if any)
    const cached = localStorage.getItem("clone_workflows_cache");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setWorkFlowData(parsed);
        }
      } catch (e) {
        console.error("Error parsing cached workflows", e);
      }
    }

    // 2) fetch latest in background
    getExistingWorkflows();
    getData();
  }, []);

  const getData = async () => {
    try {
      const url = `${REACT_APP_API_BASE_URL}/get_workflows?user_id=${getCookie(
        "sub"
      )}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          access_token: AuthService.getAccessToken(),
        },
      });

      const tempArr = (response.data?.data || []).slice().reverse();

      // read previous cached array (from state OR localStorage)
      const prev =
        workFlowdata && workFlowdata.length
          ? workFlowdata
          : (() => {
            const cached = localStorage.getItem("clone_workflows_cache");
            if (!cached) return [];
            try {
              const parsed = JSON.parse(cached);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          })();

      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(tempArr);

      // only update state + cache if changed
      if (prevStr !== nextStr) {
        setWorkFlowData(tempArr);
        localStorage.setItem("clone_workflows_cache", nextStr);
      }
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
    }
  };

  const prefetchWorkflows = () => {
    queryClient.prefetchQuery({
      queryKey: ["workflows", getCookie("sub")],
      queryFn: fetchWorkflows,
    });
  };

  // This is only for name duplicate check (wf_name_check equivalent)
  const { data: fetchedworkflows = [] } = useQuery({
    queryKey: ["workflows", getCookie("sub")],
    queryFn: fetchWorkflows,
    staleTime: 60000,
    enabled: true,
  });

  // 🔁 Re‑run name duplicate validation when name changes (e.g. after clone)
  useEffect(() => {
    const name = formData.stage1?.workflow_name?.trim();
    if (!name || !refLblerrWorkflowName.current) return;

    const alreadyExists = fetchedworkflows.some(
      (wf) => wf.toLowerCase() === name.toLowerCase()
    );

    if (alreadyExists) {
      refLblerrWorkflowName.current.textContent =
        "Workflow name already exists!";
      refLblerrWorkflowName.current.classList.remove("hidden");
    } else {
      refLblerrWorkflowName.current.classList.add("hidden");
    }
  }, [formData.stage1?.workflow_name, fetchedworkflows]);

  const handleWorkflowNameChange = (e) => {
    console.log("formData before name change:", formData);

    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[\r\n]+/g, " ");
    const alreadyExists = fetchedworkflows.some(
      (wf) => wf.toLowerCase() === sanitizedValue.toLowerCase()
    );

    updateFormData("stage1", {
      ...formData.stage1,
      [name]: sanitizedValue,
    });

    if (alreadyExists) {
      refLblerrWorkflowName.current.textContent =
        "Workflow name already exists!";
      refLblerrWorkflowName.current.classList.remove("hidden");
    } else {
      refLblerrWorkflowName.current.classList.add("hidden");
    }
  };

  const handleWorkflowDescriptionChange = (e) => {
    const { name, value } = e.target;
    updateFormData("stage1", {
      ...formData.stage1,
      [name]: value,
    });
  };

  const handleUploadOptionChange = (e) => {
    const selectedOption = e.target.value;
    setUploadOption(selectedOption);
    updateFormData("stage1", {
      ...formData.stage1,
      file_upload_option: selectedOption === "upload_new" ? "New" : "Existing",
    });

    if (selectedOption === "use_existing") {
      getExistingWorkflows();
    }
  };

  const getExistingWorkflows = async () => {
    const user_id = getCookie("sub");
    const url = `${REACT_APP_API_BASE_URL}/getSalesDataInfo?user_id=${user_id}`;
    try {
      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: AuthService.getAccessToken(),
          access_token: AuthService.getAccessToken(),
        },
      });
      setExistingWorkflowData(response.data.data);
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
    }
  };


  const handleDeleteDataset = async (data_upload_id) => {
    try {
      const url = `${REACT_APP_API_BASE_URL}/qc/delete/${data_upload_id}`;
      const response = await axios.delete(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: AuthService.getAccessToken(),
          access_token: AuthService.getAccessToken(),
        },
      });

      // Remove the deleted item from the state
      setExistingWorkflowData(
        existingWorkFlowData.filter(item => item.data_upload_id !== data_upload_id)
      );

      // Also clear the selection if the deleted dataset was selected
      if (formData.stage1?.data_upload_id === data_upload_id) {
        updateFormData("stage1", {
          ...formData.stage1,
          data_upload_id: null,
        });
      }

      console.log("Dataset deleted successfully:", response.data);
    } catch (error) {
      console.error("Error deleting dataset:", error.message);
    }
  };

  const handleRowClick = (data_upload_id) => {
    updateFormData("stage1", {
      ...formData.stage1,
      data_upload_id: data_upload_id,
    });
  };

  const forecastTemplates = [
    { label: "New Product Launch", value: "new_product" },
    { label: "Seasonal Promotion", value: "seasonal_promotion" },
    { label: "Stable Product Line", value: "stable_product" },
    { label: "Start from Scratch", value: "scratch" },
  ];

  const handleTemplateChange = (e) => {
    updateFormData("stage1", {
      ...formData.stage1,
      forecast_template: e.target.value,
    });
  };

  const handleNext = () => {
    if (!formData.stage1?.workflow_name) {
      refLblerrWorkflowName.current.textContent = "* Required";
      refLblerrWorkflowName.current.classList.remove("hidden");
      refWorkflowName.current.focus();
      return;
    }
    setActiveTab("Stage2");
  };

  return (
    <ErrorBoundary>
      <div style={stageLayoutStyles.wrapper}>
        <div style={stageLayoutStyles.card}>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            style={{ marginBottom: 14 }}
          >
            <div className="flex flex-col">
              <label style={stageFormStyles.label}>
                Forecast Name <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={refWorkflowName}
                name="workflow_name"
                style={{
                  ...stageFormStyles.textarea,
                  minHeight: 56,
                  height: 56,
                  resize: "none",
                  boxSizing: "border-box",
                }}
                placeholder="Enter Here"
                value={formData.stage1?.workflow_name || ""}
                onChange={handleWorkflowNameChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                onFocus={prefetchWorkflows}
                autoComplete="off"
                rows={2}
              />
              <span
                ref={refLblerrWorkflowName}
                className="hidden"
                style={stageFormStyles.errorText}
              >
                * Required
              </span>
            </div>

            <div className="flex flex-col">
              <label style={stageFormStyles.label}>
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="workflow_description"
                style={{
                  ...stageFormStyles.textarea,
                  minHeight: 56,
                  height: 56,
                  resize: "none",
                  boxSizing: "border-box",
                }}
                placeholder="Enter Here"
                value={formData.stage1?.workflow_description || ""}
                onChange={handleWorkflowDescriptionChange}
                rows={2}
              />
            </div>
          </div>

          <div style={stageFormStyles.fieldGroup}>
            <h3 style={stageFormStyles.sectionTitle}>
              Forecast Options
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex items-center gap-3 border border-[#e6eef4] bg-[#f6fafd] hover:bg-[#eff7fc] px-4 py-3 rounded-lg w-full transition"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#0c3c54",
                }}
              >
                <iconify-icon
                  icon="mdi:content-copy"
                  width="22"
                  height="22"
                />
                Clone Existing Forecast
              </button>

              <WorkflowTableModal
                isOpen={open}
                onClose={() => setOpen(false)}
                workFlowPageData={workFlowdata}
              />
            </div>
          </div>

          {/* Upload Option Toggle */}
          <div style={stageFormStyles.fieldGroup}>
            <h3 style={stageFormStyles.sectionTitle}>
              Data Source
            </h3>
            <div className="flex items-center gap-8 mb-4">
              <label
                className="wf-option-label flex items-center gap-2 cursor-pointer text-[#0c3c54]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <input
                  type="radio"
                  name="upload_option"
                  value="upload_new"
                  checked={uploadOption === "upload_new"}
                  onChange={handleUploadOptionChange}
                  className="w-4 h-4 accent-[#202b70]"
                />
                Upload New
              </label>
              <label
                className="wf-option-label flex items-center gap-2 cursor-pointer text-[#0c3c54]"
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <input
                  type="radio"
                  name="upload_option"
                  value="use_existing"
                  checked={uploadOption === "use_existing"}
                  onChange={handleUploadOptionChange}
                  className="w-4 h-4 accent-[#202b70]"
                />
                Use Existing
              </label>
            </div>

            {uploadOption === "use_existing" ? (
              <div className="border border-[#032B4E] rounded-lg overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#032B4E] text-white">
                    <tr>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Dataset Name
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Data Cadence
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Start Date
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        End Date
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Remarks
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Select
                      </th>
                      <th
                        className="py-2 px-3"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#f7f8fa] text-[#333]">
                    {existingWorkFlowData && existingWorkFlowData.length > 0 ? (
                      existingWorkFlowData.map((item, index) => (
                        <tr
                          key={index}
                          className="border-t border-gray-200 hover:bg-[#e8f0fe] transition cursor-pointer"
                          onClick={() => handleRowClick(item.data_upload_id)}
                        >
                          <td
                            className="py-2 px-3"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          >
                            {item.dataset_name}
                          </td>
                          <td
                            className="py-2 px-3"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          >
                            {item.data_cadence}
                          </td>
                          <td
                            className="py-2 px-3"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          >
                            {item.start_date}
                          </td>
                          <td
                            className="py-2 px-3"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          >
                            {item.end_date}
                          </td>
                          <td
                            className="py-2 px-3"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          >
                            {item.remark}
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="radio"
                              name="clone_forecast_group"
                              value={item.data_upload_id}
                              checked={
                                formData.stage1?.data_upload_id ===
                                item.data_upload_id
                              }
                              onChange={() =>
                                updateFormData("stage1", {
                                  ...formData.stage1,
                                  data_upload_id: item.data_upload_id,
                                })
                              }
                              className="form-radio text-[#202b70] accent-[#202b70] h-4 w-4 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDataset(item.data_upload_id);
                              }}
                              className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-semibold"
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: 600,
                                fontSize: 12,
                              }}
                            >
                              <iconify-icon
                                icon="mdi:delete"
                                width="16"
                                height="16"
                                className="inline-block"
                              />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="text-center py-4 text-[#555]"
                          style={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: 12,
                            fontWeight: 400,
                          }}
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setOpenQC(true)}
                  type="button"
                  className="flex items-center justify-center gap-2 border border-[#032B4E] bg-[#6b7280] px-4 py-3 rounded-lg w-full transition text-white"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <iconify-icon icon="mdi:upload" width="22" height="22" />
                  Upload dataset
                </button>
                <QCModal open={openQC} onClose={() => setOpenQC(false)}>
                  <Base workflow_name={refWorkflowName.current.value} description={formData.stage1?.workflow_description} />
                </QCModal>
              </div>
            )}
          </div>

          <StageNavButtons showBack={false} onNext={handleNext} />
        </div>
      </div>
    </ErrorBoundary>
  );
}

